import { setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetOwnershipAccount, useUpdateOwnershipAccountMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { z } from "zod";

export const OWNERSHIP_FORM_ID = "ownership-account-form";
const ZIP_REGEX = /^[0-9]{5}(-[0-9]{4})?$/;
const ADDRESS_FIELDS = ["addressLine1", "addressLine2", "pobox", "city", "state", "zipCode"];

const schema = z
  .object({
    correspondenceAddressSource: z.enum(["PROPERTY", "OWNER_PRIMARY", "CUSTOM"]),
    distribution: z.enum(["ALL", "PRIMARY"]),
    customAddress: z.object(Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, z.string().optional().nullable()]))),
  })
  .superRefine((data, ctx) => {
    if (data.correspondenceAddressSource !== "CUSTOM") return;
    const a: any = data.customAddress || {};
    ([["addressLine1", "Please enter Address Line 1."], ["city", "Please enter City."],
      ["state", "Please select State."], ["zipCode", "Please enter ZIP Code."]] as const)
      .forEach(([field, message]) => {
        if (!a[field]?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["customAddress", field] });
      });
    if (a.zipCode?.trim() && !ZIP_REGEX.test(a.zipCode.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter ZIP code as XXXXX or XXXXX-XXXX.", path: ["customAddress", "zipCode"] });
    }
  });

const useOwnershipDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { id: ownershipId = "" } = useParams();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = communityData?.communityId;
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const stateList = useSelector((state: RootState) => state.currentData.stateData) || [];
  const isAdmin = location.pathname.startsWith("/admin");
  const canEdit = isAdmin || (location.pathname.startsWith("/cam") && !!masterDataPermission?.update);
  const [isEditing, setIsEditing] = useState(false);

  const { data, isLoading } = useGetOwnershipAccount({ communityId, ownershipId });
  const account = data?.data;

  const toStateCode = (v?: string) => stateList.find((s: any) => s.name == v || s.code == v)?.code || v || "";
  const toStateName = (v?: string) => stateList.find((s: any) => s.code == v)?.name || v || null;
  const stateOptions = useMemo(() => {
    const options = stateList.map((s: any) => ({ label: s.name, value: s.code }));
    const raw = account?.customAddress?.state;
    if (raw && !options.some((o: any) => o.value == toStateCode(raw))) options.push({ label: raw, value: raw });
    return options;
  }, [stateList, account]);

  const form = useForm<any>({ resolver: zodResolver(schema) });
  const resetForm = () => form.reset({
    correspondenceAddressSource: account?.correspondenceAddressSource || "PROPERTY",
    distribution: account?.multiCorrespondence === false ? "PRIMARY" : "ALL",
    customAddress: Object.fromEntries(ADDRESS_FIELDS.map((f) => [
      f, f === "state" ? toStateCode(account?.customAddress?.state) : account?.customAddress?.[f] || "",
    ])),
  });
  useEffect(() => { if (account) resetForm(); }, [account, stateList]);

  const { mutate: updateAccount, isPending: isSaving } = useUpdateOwnershipAccountMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string" ? error?.response?.data?.message : "Something went wrong"
      );
    },
    onSuccess: () => {
      toast.success("Ownership updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["ownership-account", communityId, ownershipId] });
      queryClient.invalidateQueries({ queryKey: ["ownership-grid"] });
      setIsEditing(false);
    },
  });

  const onSubmit = (values: any) => {
    const custom = values.correspondenceAddressSource === "CUSTOM"
      ? {
          ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, values.customAddress?.[f]?.trim() || null])),
          state: toStateName(values.customAddress?.state),
        }
      : null;
    updateAccount({
      communityId,
      ownershipId,
      data: {
        correspondenceAddressSource: values.correspondenceAddressSource,
        multiCorrespondence: values.distribution === "ALL",
        customAddress: custom,
      },
    });
  };

  // Sibling pages under /<admin|cam>/master-data/
  const masterBase = location.pathname.replace(/\/property-ownership\/.*$/, "");
  const openOwner = (userId: number) => navigate(`${masterBase}/${routes.owner}/${routes.details}/${userId}`);
  const openProperty = () => {
    if (!account?.propertyId) return;
    dispatch(setPropertyData({ propertyId: account.propertyId, propertyName: account.propertyName }));
    navigate(`${masterBase}/${routes.properties}/${account.propertyId}/view`);
  };

  return {
    account, isLoading, canEdit, isEditing,
    startEdit: () => setIsEditing(true),
    cancelEdit: () => { resetForm(); setIsEditing(false); },
    form, onSubmit, isSaving, stateOptions,
    onBack: () => navigate(`${masterBase}/${routes.propertyOwnership}`),
    openOwner, openProperty,
  };
};

export default useOwnershipDetail;
